import {testIdProps} from '@github-ui/test-id-props'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {FormControl, IconButton, Link, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useId, useRef, useState} from 'react'

import {SettingsFieldCustomTypeRenameUI, SettingsFieldRename} from '../api/stats/contracts'
import {EmojiAutocomplete} from '../components/common/emoji-autocomplete'
import {replaceShortCodesWithEmojis} from '../helpers/emojis'
import {useThemedMediaUrl} from '../helpers/media-urls'
import {usePostStats} from '../hooks/common/use-post-stats'
import type {ColumnModel} from '../models/column-model'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useReservedColumnNames} from '../state-providers/columns/use-reserved-column-names'
import {useSetColumnName} from '../state-providers/columns/use-set-column-name'
import {RenameProjectFieldDialogResources} from '../strings'
import {validateColumnTitle} from '../validations/validate-column-title'
import styles from './rename-project-field-dialog.module.css'

type RenameProjectFieldDialogProps = {
  onConfirm: () => void
  onCancel: () => void
  onBack?: () => void
  column: ColumnModel
  variant?: 'issue-field' | 'issue-type'
}

type VariantConfig = {
  defaultName: string
  dialogClassName?: string
  dialogBodyClassName?: string
  resources: {
    title: string
    description: string
    inputLabel: string
    confirmButtonLabel: string
    cancelButtonLabel: string
    fieldLabel?: string
    learnMoreLink?: string
    learnMoreText?: string
    videoDescription?: string
  }
  showVideo: boolean
}

const getVariantConfig = (variant: 'issue-field' | 'issue-type', columnName: string): VariantConfig => {
  if (variant === 'issue-type') {
    const variantResources = RenameProjectFieldDialogResources.IssueTypeVariant
    return {
      defaultName: variantResources.defaultInputString,
      dialogClassName: styles.issueTypeRenameDialog,
      dialogBodyClassName: styles.dialogBody,
      resources: {
        title: variantResources.title,
        description: variantResources.description(columnName),
        inputLabel: variantResources.fieldLabel,
        confirmButtonLabel: variantResources.actionPrimary,
        cancelButtonLabel: variantResources.actionSecondary,
        fieldLabel: variantResources.fieldLabel,
        learnMoreLink: variantResources.learnMoreLink,
        learnMoreText: variantResources.learnMoreText,
        videoDescription: variantResources.videoDescription,
      },
      showVideo: true,
    }
  }

  const variantResources = RenameProjectFieldDialogResources.IssueFieldVariant
  return {
    defaultName: variantResources.defaultInputString(columnName),
    dialogClassName: undefined,
    dialogBodyClassName: undefined,
    resources: {
      title: variantResources.title,
      description: variantResources.description(columnName),
      inputLabel: variantResources.inputLabel(columnName),
      confirmButtonLabel: variantResources.actionPrimary,
      cancelButtonLabel: variantResources.actionSecondary,
    },
    showVideo: false,
  }
}

export const RenameProjectFieldDialog = ({
  onCancel,
  onConfirm,
  onBack,
  column,
  variant = 'issue-field',
}: RenameProjectFieldDialogProps) => {
  const {postStats} = usePostStats()
  const {reservedColumnNames} = useReservedColumnNames()
  const {allColumns} = useAllColumns()
  const {updateName} = useSetColumnName()
  const textInputRef = useRef<HTMLInputElement>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const videoDescriptionId = useId()

  const config = getVariantConfig(variant, column.name)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [name, setName] = useState(config.defaultName)

  const issueTypeMediaUrl = useThemedMediaUrl('issueTypes', 'demo')

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(replaceShortCodesWithEmojis(e.currentTarget.value))
      if (hasAttemptedSubmit) {
        setHasAttemptedSubmit(false)
      }
    },
    [hasAttemptedSubmit],
  )

  const {isValid: isTitleValid, message: validationMessage} = validateColumnTitle(
    {allColumns, reservedColumnNames},
    name,
  )

  const showValidationErrors = hasAttemptedSubmit && !isSubmitting

  const hasChanges = name !== column.name
  const isFormValid = isTitleValid && hasChanges

  const onUpdateColumnName = useCallback(async () => {
    setHasAttemptedSubmit(true)
    if (isSubmitting || !isFormValid) return

    setIsSubmitting(true)
    try {
      await updateName(column, name)

      postStats({
        name: SettingsFieldRename,
        ui: SettingsFieldCustomTypeRenameUI,
        context: `new name: ${name}, original name: ${column.name}`,
        memexProjectColumnId: column.id,
      })

      onConfirm()
    } finally {
      setIsSubmitting(false)
    }
  }, [updateName, name, isFormValid, isSubmitting, onConfirm, column, postStats])

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    }
  }, [onBack])

  const dialogTitle = onBack ? (
    <div className={styles.titleWithBack}>
      <IconButton
        aria-label="Go back"
        icon={ArrowLeftIcon}
        variant="invisible"
        onClick={handleBack}
        className={styles.backButton}
        {...testIdProps('rename-column-back-button')}
      />
      <span>{config.resources.title}</span>
    </div>
  ) : (
    config.resources.title
  )

  const FormControlSection = (
    <FormControl className={config.showVideo ? styles.formControl : undefined}>
      <FormControl.Label>{config.resources.inputLabel}</FormControl.Label>
      <EmojiAutocomplete fullWidth>
        <TextInput
          ref={textInputRef}
          value={name}
          onChange={handleNameChange}
          onFocus={e => e.currentTarget.select()}
          aria-invalid={!isTitleValid}
          validationStatus={!isTitleValid && showValidationErrors ? 'error' : undefined}
          {...testIdProps('update-column-name-input')}
        />
      </EmojiAutocomplete>
      {!isTitleValid && showValidationErrors && (
        <FormControl.Validation variant="error">{validationMessage}</FormControl.Validation>
      )}
    </FormControl>
  )

  return (
    <Dialog
      onClose={onCancel}
      title={dialogTitle}
      height="auto"
      width="large"
      className={config.dialogClassName}
      initialFocusRef={textInputRef}
      footerButtons={[
        {
          buttonType: 'default',
          content: config.resources.cancelButtonLabel,
          onClick: onCancel,
          ...testIdProps('rename-column-button-cancel'),
        },
        {
          buttonType: 'primary',
          content: config.resources.confirmButtonLabel,
          onClick: onUpdateColumnName,
          loading: isSubmitting,
          ...testIdProps('rename-column-button'),
        },
      ]}
      {...testIdProps('rename-project-field-dialog')}
      renderBody={() => {
        return (
          <Dialog.Body className={config.dialogBodyClassName}>
            {config.showVideo ? (
              <>
                <div className={clsx(styles.contentBox, styles.contentSection)}>
                  <p>
                    {config.resources.description}&nbsp;
                    {config.resources.learnMoreLink && (
                      <Link target="_blank" rel="noopener noreferrer" href={config.resources.learnMoreLink}>
                        {config.resources.learnMoreText}
                      </Link>
                    )}
                  </p>
                  {FormControlSection}
                </div>
                <div className={styles.videoSection}>
                  <div className={styles.videoContainer}>
                    <video
                      aria-describedby={videoDescriptionId}
                      controls
                      playsInline
                      autoPlay={!prefersReducedMotion}
                      muted
                      loop
                      className={styles.video}
                      {...testIdProps('issue-type-rename-video')}
                    >
                      <source src={issueTypeMediaUrl} type="video/mp4" />
                    </video>
                    <div id={videoDescriptionId} className={clsx('sr-only', styles.srOnly)}>
                      {config.resources.videoDescription}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p>{config.resources.description}</p>
                {FormControlSection}
              </>
            )}
          </Dialog.Body>
        )
      }}
    />
  )
}
